import ListItem from "@/components/ListItem";
import Progress from "@/components/Progress";
import Tag from "@/components/Tag";
import { getExperienceIndexCompetencyIcon } from "./experienceIndexIcons";
import {
  EXPERIENCE_INDEX_SCORE_MAX,
  formatExperienceIndexHubScore,
  type CompetencyStandingRow
} from "./experienceIndexResults";

const PRIORITY_TOOLTIP =
  "Priority for the selected comparison role — one of the six competencies this role values most on its rubric.";

/** Keep Score and the bar aligned, inset from Priority / the chevron. ListItem already uses `gap-2`. */
const SCORE_TRAILING_INSET = "pr-3";
/** Absolute Progress/tick use this in-flow track — padding on the relative parent does not inset them. */
const SCORE_BAR_TRACK = "relative h-full w-[calc(100%-0.75rem)]";

function scorePercent(value: number) {
  return Math.max(0, Math.min(100, (value / EXPERIENCE_INDEX_SCORE_MAX) * 100));
}

function standingAccessibilityLabel(row: CompetencyStandingRow, canViewAnswers: boolean) {
  const parts = [row.label];

  if (!row.isComplete) {
    parts.push("Not yet answered");
  } else {
    parts.push(formatExperienceIndexHubScore(row.memberScore ?? 0));
    if (row.desiredScore != null) {
      parts.push(`role rubric ${row.desiredScore} out of 5`);
    }
  }

  if (row.isPriority) {
    parts.push("Priority");
  }

  if (canViewAnswers) {
    parts.push("View answers");
  }

  return parts.join(", ");
}

function ScoreBar({ row }: { row: CompetencyStandingRow }) {
  return (
    <div
      className="relative mt-1 h-4 w-full"
      data-testid={`career-plan-competency-score-bar-${row.id}`}
      aria-hidden="true"
    >
      <div className={SCORE_BAR_TRACK}>
        <div className="absolute inset-x-0 top-1/2 h-1 -translate-y-1/2">
          <Progress
            variant="linear"
            value={scorePercent(row.memberScore ?? 0) / 100}
            tone="success"
            className="h-full w-full"
          />
        </div>
        {row.desiredScore != null ? (
          <span
            className="pointer-events-none absolute top-0 z-10 h-4 w-0.5 rounded-full bg-sys-brand-on-surface ring-1 ring-sys-brand-surface"
            style={{ left: `${scorePercent(row.desiredScore)}%`, transform: "translateX(-50%)" }}
            data-testid={`career-plan-radar-benchmark-${row.id}`}
          />
        ) : null}
      </div>
    </div>
  );
}

export default function ExperienceIndexCompetencyStandingRow({
  row,
  onViewAnswers
}: {
  row: CompetencyStandingRow;
  onViewAnswers?: (competencyId: string) => void;
}) {
  return (
    <div data-testid={`career-plan-competency-standing-${row.id}`}>
      <ListItem
        compact
        className={onViewAnswers ? "rounded-lg hover:bg-sys-brand-surface-container-low" : undefined}
        ariaLabel={standingAccessibilityLabel(row, Boolean(onViewAnswers))}
        iconName={getExperienceIndexCompetencyIcon(row.label)}
        iconBackground
        headline={
          row.isComplete ? (
            <span className={`flex w-full min-w-0 items-baseline justify-between gap-2 ${SCORE_TRAILING_INSET}`}>
              <span className="min-w-0">{row.label}</span>
              <span className="shrink-0 text-label-medium font-normal">
                {formatExperienceIndexHubScore(row.memberScore ?? 0)}
              </span>
            </span>
          ) : (
            row.label
          )
        }
        supportingText={
          row.isComplete ? (
            <ScoreBar row={row} />
          ) : (
            <Tag tagType="surface" size="small">
              Not yet answered
            </Tag>
          )
        }
        tagText={row.isPriority ? "Priority" : undefined}
        tagType="primary"
        tagStyle="standard"
        tagPosition="trailing"
        tagTitle={row.isPriority ? PRIORITY_TOOLTIP : undefined}
        tagTestId={row.isPriority ? "career-plan-competency-priority" : undefined}
        action={onViewAnswers ? () => onViewAnswers(row.id) : undefined}
      />
    </div>
  );
}
